/**
 * Batch #14.34 — Illustrations SVG style "capture d'écran" pour le guide Immeuble.
 *
 * Chaque composant retourne un SVG qui mime visuellement une vraie capture du
 * portail (couleurs, tonalités, structure). Avantages :
 * - Pas de données seedées requises
 * - Pas d'images à uploader
 * - Vectoriel = ultra-net sur tous les écrans
 * - Cohérent avec la palette de l'app (cyan/teal/emerald/etc.)
 *
 * Si plus tard vous voulez de vraies captures, déposez-les dans
 * /public/guides/immeuble/{slug}.png — le composant Screenshot fera le fallback
 * automatique (cf. ImmeubleGuide.jsx).
 */

import { Pill } from "./WebGuideIllustrations";

const SCREEN = { w: 1200, h: 720, radius: 16 };

// ------------------------------------------------------------------
//  Helpers communs
// ------------------------------------------------------------------
const Frame = ({ children, accent = "#0E7490" }) => (
  <svg viewBox={`0 0 ${SCREEN.w} ${SCREEN.h}`} className="w-full h-auto bg-white" preserveAspectRatio="xMidYMid meet">
    <defs>
      <linearGradient id="hero-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={accent} />
        <stop offset="100%" stopColor="#0F766E" />
      </linearGradient>
      <filter id="cardshadow" x="-2%" y="-2%" width="104%" height="108%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.08" />
      </filter>
    </defs>
    {/* Window chrome */}
    <rect x="0" y="0" width={SCREEN.w} height={SCREEN.h} fill="#F8FAFC" />
    <rect x="0" y="0" width={SCREEN.w} height="40" fill="#E2E8F0" />
    <circle cx="22" cy="20" r="6" fill="#EF4444" />
    <circle cx="44" cy="20" r="6" fill="#F59E0B" />
    <circle cx="66" cy="20" r="6" fill="#10B981" />
    <rect x="100" y="11" width="980" height="18" rx="9" fill="#F1F5F9" />
    <text x="120" y="24" fontFamily="ui-monospace, monospace" fontSize="11" fill="#64748B">portal.dgcomptabilite.ca</text>
    {children}
  </svg>
);

const Text = ({ x, y, children, size = 13, weight = 400, color = "#334155", anchor = "start" }) => (
  <text x={x} y={y} fontFamily="system-ui, sans-serif" fontSize={size} fontWeight={weight} fill={color} textAnchor={anchor}>
    {children}
  </text>
);

const Btn = ({ x, y, w, label, fill = "#0891B2", textFill = "white" }) => (
  <g>
    <rect x={x} y={y} width={w} height="36" rx="8" fill={fill} />
    <text x={x + w / 2} y={y + 23} fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="600" fill={textFill} textAnchor="middle">{label}</text>
  </g>
);

// ==================================================================
//  1. Logements — Vue d'ensemble (liste cards)
// ==================================================================
export function LogementsListe() {
  return (
    <Frame accent="#0E7490">
      {/* Header */}
      <rect x="0" y="40" width={SCREEN.w} height="80" fill="url(#hero-grad)" />
      <Text x="40" y="80" size="22" weight="700" color="white">🏠 Mes logements</Text>
      <Text x="40" y="100" size="13" color="#A5F3FC">3 immeubles · 7 unités · 6 louées</Text>
      <Btn x="1050" y="68" w="130" label="+ Logement" fill="white" textFill="#0E7490" />

      {/* Card grid */}
      {[
        { y: 160, addr: "412 Jean-Routier", units: 3, occupied: 3, rev: "4 850 $" },
        { y: 320, addr: "1234 Beauport", units: 2, occupied: 2, rev: "2 600 $" },
        { y: 480, addr: "78 St-Laurent", units: 2, occupied: 1, rev: "1 750 $" },
      ].map((c, i) => (
        <g key={i} filter="url(#cardshadow)">
          <rect x="40" y={c.y} width="1120" height="120" rx="12" fill="white" stroke="#E2E8F0" />
          {/* Address & badge */}
          <rect x="60" y={c.y + 24} width="60" height="60" rx="10" fill="#CFFAFE" />
          <text x="90" y={c.y + 62} fontSize="28" textAnchor="middle">🏠</text>
          <Text x="140" y={c.y + 44} size="17" weight="700" color="#1E293B">{c.addr}</Text>
          <Text x="140" y={c.y + 64} size="12" color="#64748B">Québec, QC · Immeuble locatif</Text>
          {/* Stats */}
          <Text x="140" y={c.y + 92} size="11" color="#475569">📋 {c.units} unités</Text>
          <Text x="240" y={c.y + 92} size="11" color="#475569">👥 {c.occupied}/{c.units} occupées</Text>
          <Text x="380" y={c.y + 92} size="11" color="#475569">💰 {c.rev} / mois</Text>
          {/* Status pill */}
          {c.occupied === c.units ? (
            <Pill x="1020" y={c.y + 24} w="120" label="✓ Complet" fill="#10B981" />
          ) : (
            <Pill x="1020" y={c.y + 24} w="120" label="● Vacance" fill="#F59E0B" />
          )}
          <Text x="1085" y={c.y + 96} size="11" weight="600" color="#0891B2" anchor="middle">Voir détail →</Text>
        </g>
      ))}
    </Frame>
  );
}

// ==================================================================
//  2. Bail — Formulaire création
// ==================================================================
export function BailCreation() {
  return (
    <Frame accent="#4F46E5">
      <rect x="0" y="40" width={SCREEN.w} height="60" fill="#4F46E5" />
      <Text x="40" y="76" size="18" weight="700" color="white">📝 Nouveau bail TAL — 412 Jean-Routier, Apt 1</Text>

      <rect x="40" y="120" width="1120" height="560" rx="12" fill="white" stroke="#E2E8F0" filter="url(#cardshadow)" />

      {/* Section: Locataire */}
      <Text x="70" y="160" size="14" weight="700" color="#1E293B">👤 LOCATAIRE</Text>
      <line x1="70" y1="170" x2="1130" y2="170" stroke="#E2E8F0" />
      {[
        { x: 70, y: 195, label: "Prénom", value: "Marie" },
        { x: 380, y: 195, label: "Nom", value: "Tremblay" },
        { x: 690, y: 195, label: "Téléphone", value: "418 555-0142" },
        { x: 70, y: 260, label: "Courriel", value: "marie.tremblay@gmail.com", w: 590 },
        { x: 690, y: 260, label: "Nb de personnes", value: "2 adultes + 1 enfant" },
      ].map((f, i) => (
        <g key={i}>
          <Text x={f.x} y={f.y} size="10" weight="600" color="#64748B">{f.label.toUpperCase()}</Text>
          <rect x={f.x} y={f.y + 8} width={f.w || 290} height="36" rx="8" fill="#F8FAFC" stroke="#CBD5E1" />
          <Text x={f.x + 12} y={f.y + 30} size="13" color="#1E293B">{f.value}</Text>
        </g>
      ))}

      {/* Section: Bail */}
      <Text x="70" y="340" size="14" weight="700" color="#1E293B">📅 PÉRIODE & LOYER</Text>
      <line x1="70" y1="350" x2="1130" y2="350" stroke="#E2E8F0" />
      {[
        { x: 70, y: 375, label: "Date début", value: "1er juillet 2026" },
        { x: 380, y: 375, label: "Date fin", value: "30 juin 2027" },
        { x: 690, y: 375, label: "Loyer mensuel", value: "1 250 $", weight: 700 },
        { x: 70, y: 440, label: "Mode de paiement", value: "PAD (prélèvement auto)" },
        { x: 380, y: 440, label: "Jour de paiement", value: "1er du mois" },
        { x: 690, y: 440, label: "Dépôt de sécurité", value: "Non requis (QC)" },
      ].map((f, i) => (
        <g key={i}>
          <Text x={f.x} y={f.y} size="10" weight="600" color="#64748B">{f.label.toUpperCase()}</Text>
          <rect x={f.x} y={f.y + 8} width="290" height="36" rx="8" fill="#F8FAFC" stroke="#CBD5E1" />
          <Text x={f.x + 12} y={f.y + 30} size="13" color="#1E293B" weight={f.weight}>{f.value}</Text>
        </g>
      ))}

      {/* CTA */}
      <Btn x="900" y="600" w="220" label="📤 Envoyer pour signature" fill="#10B981" />
      <Btn x="70" y="600" w="160" label="Enregistrer brouillon" fill="#F1F5F9" textFill="#475569" />
    </Frame>
  );
}

// ==================================================================
//  3. Paiements — Vue mensuelle
// ==================================================================
export function PaiementsVue() {
  const rows = [
    { tenant: "Marie Tremblay", apt: "412-1", method: "💳 PAD", date: "2026-02-01", amount: "1 250 $", status: "Payé", color: "#10B981" },
    { tenant: "Jean Lavoie", apt: "412-2", method: "💳 Carte", date: "2026-02-01", amount: "1 100 $", status: "Payé", color: "#10B981" },
    { tenant: "Sophie Roy", apt: "412-3", method: "🏦 Interac", date: "2026-02-03", amount: "1 350 $", status: "Payé", color: "#10B981" },
    { tenant: "Marc Bouchard", apt: "1234-1", method: "📝 Chèque", date: "—", amount: "1 400 $", status: "En retard", color: "#EF4444" },
    { tenant: "Léa Dubois", apt: "1234-2", method: "💳 PAD", date: "2026-02-01", amount: "1 200 $", status: "Payé", color: "#10B981" },
    { tenant: "Tom Lévesque", apt: "78-1", method: "🏦 Interac", date: "2026-02-05", amount: "1 750 $", status: "Partiel", color: "#F59E0B" },
  ];
  return (
    <Frame accent="#10B981">
      <rect x="0" y="40" width={SCREEN.w} height="80" fill="url(#hero-grad)" />
      <Text x="40" y="80" size="22" weight="700" color="white">💰 Paiements de loyer — Février 2026</Text>
      <Text x="40" y="100" size="13" color="#A7F3D0">5 reçus · 1 en retard · Total : 8 050 $ / 8 050 $</Text>

      <rect x="40" y="160" width="1120" height="520" rx="12" fill="white" stroke="#E2E8F0" filter="url(#cardshadow)" />

      {/* Header */}
      <rect x="40" y="160" width="1120" height="40" rx="12" fill="#F8FAFC" />
      <Text x="60" y="186" size="11" weight="700" color="#64748B">LOCATAIRE</Text>
      <Text x="280" y="186" size="11" weight="700" color="#64748B">UNITÉ</Text>
      <Text x="380" y="186" size="11" weight="700" color="#64748B">MODE</Text>
      <Text x="540" y="186" size="11" weight="700" color="#64748B">DATE REÇUE</Text>
      <Text x="720" y="186" size="11" weight="700" color="#64748B">MONTANT</Text>
      <Text x="900" y="186" size="11" weight="700" color="#64748B">STATUT</Text>
      <Text x="1080" y="186" size="11" weight="700" color="#64748B">ACTION</Text>

      {/* Rows */}
      {rows.map((r, i) => (
        <g key={i}>
          <line x1="40" y1={200 + i * 70} x2="1160" y2={200 + i * 70} stroke="#F1F5F9" />
          <rect x="56" y={216 + i * 70} width="32" height="32" rx="16" fill="#E0E7FF" />
          <text x="72" y={236 + i * 70} fontSize="13" fontWeight="700" fill="#4F46E5" textAnchor="middle">{r.tenant[0]}</text>
          <Text x="100" y={234 + i * 70} size="13" weight="600" color="#1E293B">{r.tenant}</Text>
          <Text x="280" y={234 + i * 70} size="12" color="#475569">{r.apt}</Text>
          <Text x="380" y={234 + i * 70} size="12" color="#475569">{r.method}</Text>
          <Text x="540" y={234 + i * 70} size="12" color="#475569">{r.date}</Text>
          <Text x="720" y={234 + i * 70} size="13" weight="700" color="#1E293B">{r.amount}</Text>
          <Pill x="890" y={222 + i * 70} w="86" label={r.status} fill={r.color} />
          <Text x="1095" y={234 + i * 70} size="12" weight="600" color="#0891B2">Détail →</Text>
        </g>
      ))}
    </Frame>
  );
}

// ==================================================================
//  4. Coffre-fort — Catégories
// ==================================================================
export function CoffreFort() {
  const cats = [
    { x: 40, y: 160, color: "#0891B2", bg: "#CFFAFE", icon: "🏛️", title: "Taxes municipales", count: 8 },
    { x: 320, y: 160, color: "#7C3AED", bg: "#EDE9FE", icon: "🎓", title: "Taxes scolaires", count: 6 },
    { x: 600, y: 160, color: "#DC2626", bg: "#FEE2E2", icon: "🛡️", title: "Assurance bâtiment", count: 4 },
    { x: 880, y: 160, color: "#F59E0B", bg: "#FEF3C7", icon: "📋", title: "Baux signés", count: 12 },
    { x: 40, y: 380, color: "#10B981", bg: "#D1FAE5", icon: "🔧", title: "Contrats entretien", count: 5 },
    { x: 320, y: 380, color: "#0EA5E9", bg: "#E0F2FE", icon: "💧", title: "Hydro / Gaz", count: 24 },
    { x: 600, y: 380, color: "#EC4899", bg: "#FCE7F3", icon: "📐", title: "Certificats", count: 3 },
    { x: 880, y: 380, color: "#6366F1", bg: "#E0E7FF", icon: "📄", title: "Autres docs", count: 9 },
  ];
  return (
    <Frame accent="#7C3AED">
      <rect x="0" y="40" width={SCREEN.w} height="80" fill="url(#hero-grad)" />
      <Text x="40" y="80" size="22" weight="700" color="white">🗂️ Coffre-fort — 412 Jean-Routier</Text>
      <Text x="40" y="100" size="13" color="#DDD6FE">71 documents archivés · Partagé avec votre équipe</Text>

      {cats.map((c, i) => (
        <g key={i} filter="url(#cardshadow)">
          <rect x={c.x} y={c.y} width="240" height="200" rx="14" fill="white" stroke="#E2E8F0" />
          <rect x={c.x + 20} y={c.y + 22} width="60" height="60" rx="14" fill={c.bg} />
          <text x={c.x + 50} y={c.y + 64} fontSize="32" textAnchor="middle">{c.icon}</text>
          <Text x={c.x + 20} y={c.y + 110} size="15" weight="700" color="#1E293B">{c.title}</Text>
          <Text x={c.x + 20} y={c.y + 134} size="12" color="#64748B">{c.count} document{c.count > 1 ? "s" : ""}</Text>
          <line x1={c.x + 20} y1={c.y + 155} x2={c.x + 220} y2={c.y + 155} stroke="#F1F5F9" />
          <Text x={c.x + 20} y={c.y + 180} size="12" weight="600" color={c.color}>+ Déposer un fichier</Text>
        </g>
      ))}
    </Frame>
  );
}

// ==================================================================
//  5. Maintenance — Ticket détail
// ==================================================================
export function MaintenanceTicket() {
  return (
    <Frame accent="#F59E0B">
      <rect x="0" y="40" width={SCREEN.w} height="80" fill="#F59E0B" />
      <Text x="40" y="80" size="22" weight="700" color="white">🔧 Ticket #042 — Robinet qui fuit</Text>
      <Text x="40" y="100" size="13" color="#FEF3C7">Apt 412-2 · Ouvert le 12 février 2026</Text>

      {/* Left col: details */}
      <g filter="url(#cardshadow)">
        <rect x="40" y="160" width="720" height="520" rx="12" fill="white" stroke="#E2E8F0" />
        <Text x="60" y="195" size="14" weight="700" color="#1E293B">📋 Détails du ticket</Text>
        <line x1="60" y1="208" x2="740" y2="208" stroke="#F1F5F9" />
        {[
          ["Logement", "412 Jean-Routier, Apt 2"],
          ["Locataire", "Jean Lavoie · 418 555-0167"],
          ["Catégorie", "Plomberie"],
          ["Priorité", "Moyenne"],
          ["Description", "Robinet de cuisine fuit goutte-à-goutte depuis 3 jours. Pas urgent mais à régler."],
        ].map(([k, v], i) => (
          <g key={i}>
            <Text x="60" y={245 + i * 50} size="11" weight="600" color="#64748B">{k.toUpperCase()}</Text>
            <Text x="60" y={263 + i * 50} size="13" color="#1E293B">{v}</Text>
          </g>
        ))}
        {/* Status */}
        <Pill x="60" y="510" w="160" label="● En cours" fill="#3B82F6" />
        <Pill x="240" y="510" w="200" label="🛠 Plombier assigné" fill="#10B981" />
        {/* Cost */}
        <Text x="60" y="580" size="11" weight="600" color="#64748B">COÛT ESTIMÉ</Text>
        <Text x="60" y="610" size="22" weight="700" color="#10B981">125,00 $</Text>
        <Text x="60" y="630" size="11" color="#64748B">Réparation déductible 100%</Text>
      </g>

      {/* Right col: photos */}
      <g filter="url(#cardshadow)">
        <rect x="780" y="160" width="380" height="520" rx="12" fill="white" stroke="#E2E8F0" />
        <Text x="800" y="195" size="14" weight="700" color="#1E293B">📷 Photos</Text>
        <line x1="800" y1="208" x2="1140" y2="208" stroke="#F1F5F9" />
        {/* Avant */}
        <rect x="800" y="230" width="160" height="160" rx="10" fill="#FEF3C7" stroke="#F59E0B" />
        <text x="880" y="318" fontSize="56" textAnchor="middle">🚿</text>
        <Text x="880" y="408" size="11" weight="600" color="#92400E" anchor="middle">Avant</Text>
        {/* Après (placeholder) */}
        <rect x="980" y="230" width="160" height="160" rx="10" fill="#F1F5F9" stroke="#CBD5E1" strokeDasharray="4 4" />
        <text x="1060" y="320" fontSize="36" textAnchor="middle" fill="#94A3B8">+</text>
        <Text x="1060" y="408" size="11" color="#94A3B8" anchor="middle">Photo après…</Text>

        <Btn x="800" y="620" w="340" label="+ Ajouter une photo" fill="#F59E0B" />
      </g>
    </Frame>
  );
}

// ==================================================================
//  6. Dépenses — Dépôt rapide
// ==================================================================
export function DepensesRapides() {
  const fournisseurs = [
    { x: 40, y: 200, name: "Canac", logo: "🔨", color: "#DC2626", bg: "#FEE2E2" },
    { x: 250, y: 200, name: "Rona", logo: "🪜", color: "#F59E0B", bg: "#FEF3C7" },
    { x: 460, y: 200, name: "Réno-Dépôt", logo: "🛒", color: "#0891B2", bg: "#CFFAFE" },
    { x: 670, y: 200, name: "Home Depot", logo: "🧰", color: "#EA580C", bg: "#FFEDD5" },
    { x: 880, y: 200, name: "Hydro-Québec", logo: "⚡", color: "#1E3A8A", bg: "#DBEAFE" },
    { x: 40, y: 380, name: "Énergir", logo: "🔥", color: "#7C3AED", bg: "#EDE9FE" },
    { x: 250, y: 380, name: "Ville de Québec", logo: "🏛️", color: "#0F766E", bg: "#CCFBF1" },
    { x: 460, y: 380, name: "Autre fournisseur", logo: "📄", color: "#475569", bg: "#F1F5F9" },
  ];
  return (
    <Frame accent="#E11D48">
      <rect x="0" y="40" width={SCREEN.w} height="100" fill="url(#hero-grad)" />
      <Text x="40" y="84" size="22" weight="700" color="white">🧾 Dépôt rapide — Une facture à classer</Text>
      <Text x="40" y="108" size="13" color="#FECDD3">Sélectionnez le fournisseur ou uploadez directement le PDF</Text>

      {fournisseurs.map((f, i) => (
        <g key={i} filter="url(#cardshadow)">
          <rect x={f.x} y={f.y} width="180" height="160" rx="12" fill="white" stroke="#E2E8F0" />
          <rect x={f.x + 60} y={f.y + 22} width="60" height="60" rx="14" fill={f.bg} />
          <text x={f.x + 90} y={f.y + 64} fontSize="30" textAnchor="middle">{f.logo}</text>
          <Text x={f.x + 90} y={f.y + 110} size="13" weight="700" color="#1E293B" anchor="middle">{f.name}</Text>
          <Text x={f.x + 90} y={f.y + 132} size="11" color={f.color} anchor="middle">Cliquer pour upload</Text>
        </g>
      ))}

      {/* Upload zone */}
      <g>
        <rect x="670" y="380" width="490" height="160" rx="12" fill="#FEF2F2" stroke="#E11D48" strokeDasharray="6 6" />
        <text x="915" y="450" fontSize="40" textAnchor="middle">📎</text>
        <Text x="915" y="490" size="13" weight="700" color="#9F1239" anchor="middle">Glissez-déposez votre PDF ici</Text>
        <Text x="915" y="510" size="11" color="#BE123C" anchor="middle">… ou prenez en photo depuis votre téléphone</Text>
      </g>
    </Frame>
  );
}

// ==================================================================
//  7. Enquête de crédit — Formulaire
// ==================================================================
export function EnqueteCredit() {
  return (
    <Frame accent="#2563EB">
      <rect x="0" y="40" width={SCREEN.w} height="100" fill="url(#hero-grad)" />
      <Text x="40" y="84" size="22" weight="700" color="white">🛡️ Nouvelle enquête de crédit</Text>
      <Text x="40" y="108" size="13" color="#BFDBFE">Candidat locataire — Vérification d'antécédents</Text>

      <rect x="40" y="180" width="760" height="500" rx="12" fill="white" stroke="#E2E8F0" filter="url(#cardshadow)" />

      <Text x="70" y="220" size="14" weight="700" color="#1E293B">👤 INFORMATIONS DU CANDIDAT</Text>
      <line x1="70" y1="232" x2="780" y2="232" stroke="#F1F5F9" />
      {[
        { x: 70, y: 260, label: "Prénom légal", value: "Marie-Claire" },
        { x: 410, y: 260, label: "Nom légal", value: "Beaulieu-Tremblay" },
        { x: 70, y: 330, label: "Date de naissance", value: "1992-08-14" },
        { x: 410, y: 330, label: "Téléphone", value: "418 555-0188" },
        { x: 70, y: 400, label: "Courriel", value: "marie.beaulieu@example.com", w: 660 },
        { x: 70, y: 470, label: "Adresse actuelle", value: "1 rue Principale, Québec, QC G1A 1B2", w: 660 },
      ].map((f, i) => (
        <g key={i}>
          <Text x={f.x} y={f.y} size="10" weight="600" color="#64748B">{f.label.toUpperCase()}</Text>
          <rect x={f.x} y={f.y + 8} width={f.w || 320} height="36" rx="8" fill="#F8FAFC" stroke="#CBD5E1" />
          <Text x={f.x + 12} y={f.y + 30} size="13" color="#1E293B">{f.value}</Text>
        </g>
      ))}

      {/* Consentement */}
      <rect x="70" y="550" width="710" height="40" rx="8" fill="#EFF6FF" stroke="#3B82F6" />
      <rect x="84" y="562" width="16" height="16" rx="3" fill="#3B82F6" />
      <text x="92" y="575" fontSize="11" fontWeight="700" fill="white" textAnchor="middle">✓</text>
      <Text x="110" y="574" size="12" color="#1E40AF">Le candidat consent par écrit à cette enquête (obligatoire)</Text>

      {/* Right col: pricing */}
      <g filter="url(#cardshadow)">
        <rect x="820" y="180" width="340" height="500" rx="12" fill="white" stroke="#E2E8F0" />
        <rect x="820" y="180" width="340" height="70" rx="12" fill="#2563EB" />
        <Text x="990" y="220" size="14" weight="700" color="white" anchor="middle">RÉCAPITULATIF</Text>

        <Text x="840" y="290" size="11" weight="600" color="#64748B">RAPPORT</Text>
        <Text x="840" y="312" size="13" color="#1E293B">Enquête de crédit complète</Text>
        <Text x="840" y="328" size="11" color="#94A3B8">Score Equifax + antécédents</Text>

        <line x1="840" y1="360" x2="1140" y2="360" stroke="#F1F5F9" />
        <Text x="840" y="395" size="11" weight="600" color="#64748B">FRAIS</Text>
        <Text x="840" y="425" size="28" weight="700" color="#2563EB">15,00 $</Text>
        <Text x="840" y="445" size="11" color="#94A3B8">Faible coût · Tarif unique</Text>

        <Text x="840" y="490" size="11" weight="600" color="#64748B">PAYÉ PAR</Text>
        <rect x="840" y="500" width="145" height="36" rx="8" fill="#2563EB" />
        <Text x="912" y="523" size="12" weight="600" color="white" anchor="middle">✓ Propriétaire</Text>
        <rect x="995" y="500" width="145" height="36" rx="8" fill="white" stroke="#CBD5E1" />
        <Text x="1067" y="523" size="12" color="#64748B" anchor="middle">Candidat</Text>

        <Btn x="840" y="610" w="300" label="🛡️ Lancer l'enquête (15$)" fill="#2563EB" />
      </g>
    </Frame>
  );
}

// ------------------------------------------------------------------
//  Map slug → composant pour intégration dans le guide
// ------------------------------------------------------------------
export const ILLUSTRATIONS = {
  "logements-liste": LogementsListe,
  "bail-creation": BailCreation,
  "paiements-vue": PaiementsVue,
  "coffre-fort": CoffreFort,
  "maintenance-ticket": MaintenanceTicket,
  "depenses-rapides": DepensesRapides,
  "enquete-credit": EnqueteCredit,
};
